import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from './Shared';
import { patientProfileAPI } from '../services/api';

// Ιατρικές Βεβαιώσεις tab on the admin patient profile.
// Mirrors PatientReferralLettersTab: the PDF is fetched through the backend
// proxy (never a direct R2 URL) so every view is written to activity_logs.
// Cancelled notes are shown rather than hidden — for complaint handling you
// need to see that a note was issued and later withdrawn.

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleString('el-GR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const FITNESS_LABELS = {
  unfit: 'Ανίκανος προς εργασία',
  fit_with_adjustments: 'Ικανός με προσαρμογές',
  fit: 'Ικανός προς εργασία',
};

export default function PatientFitNotesTab({ patientId }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const openedRef = useRef([]);

  useEffect(() => {
    return () => {
      openedRef.current.forEach((url) => URL.revokeObjectURL(url));
      openedRef.current = [];
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await patientProfileAPI.getFitNotes(patientId);
      setNotes(res.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Αποτυχία φόρτωσης ιατρικών βεβαιώσεων');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const openPdf = async (noteId) => {
    setDownloading(noteId);
    try {
      const res = await patientProfileAPI.getFitNotePdfBlob(noteId);
      const blobUrl = URL.createObjectURL(res.data);
      openedRef.current.push(blobUrl);
      window.open(blobUrl, '_blank', 'noopener');
    } catch (err) {
      alert('Αποτυχία ανοίγματος PDF');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <Card title="📋 Ιατρικές Βεβαιώσεις">
        <p style={{ color: '#6B7280' }}>Φόρτωση…</p>
      </Card>
    );
  }

  if (error) {
    return (
      <Card title="📋 Ιατρικές Βεβαιώσεις">
        <p style={{ color: '#DC2626' }}>{error}</p>
      </Card>
    );
  }

  if (notes.length === 0) {
    return (
      <Card title="📋 Ιατρικές Βεβαιώσεις">
        <p style={{ color: '#6B7280' }}>Δεν έχουν εκδοθεί ιατρικές βεβαιώσεις για αυτόν τον ασθενή.</p>
      </Card>
    );
  }

  return (
    <Card title={`📋 Ιατρικές Βεβαιώσεις (${notes.length})`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {notes.map((n) => {
          const cancelled = n.status === 'cancelled';
          return (
            <div
              key={n.id}
              style={{
                border: `1px solid ${cancelled ? '#FCA5A5' : '#E5E7EB'}`,
                borderRadius: 8,
                padding: 16,
                background: cancelled ? '#FEF2F2' : '#FFF',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>
                    {n.fit_note_number || '—'}
                    {n.version > 1 && (
                      <span style={{ marginLeft: 8, fontSize: 12, color: '#6B7280' }}>
                        (έκδοση {n.version})
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>
                    Δρ. {n.doctor_first_name} {n.doctor_last_name}
                    {n.doctor_specialty ? ` · ${n.doctor_specialty}` : ''}
                  </div>
                </div>
                {cancelled && (
                  <span style={{
                    background: '#DC2626', color: '#FFF', fontSize: 11, fontWeight: 600,
                    padding: '3px 8px', borderRadius: 4, whiteSpace: 'nowrap',
                  }}>
                    ΑΚΥΡΩΘΗΚΕ
                  </span>
                )}
              </div>

              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 8, marginTop: 12, fontSize: 13,
              }}>
                <div>
                  <span style={{ color: '#6B7280' }}>Κατάσταση ικανότητας: </span>
                  <strong>{FITNESS_LABELS[n.fitness_status] || n.fitness_status || '—'}</strong>
                </div>
                <div>
                  <span style={{ color: '#6B7280' }}>Περίοδος: </span>
                  <strong>{fmtDate(n.start_date)} – {fmtDate(n.end_date)}</strong>
                </div>
                <div>
                  <span style={{ color: '#6B7280' }}>Ημ. έκδοσης: </span>
                  <strong>{fmtDateTime(n.issued_at || n.created_at)}</strong>
                </div>
                <div>
                  <span style={{ color: '#6B7280' }}>Ραντεβού: </span>
                  <strong>{fmtDate(n.appointment_date)} {String(n.start_time || '').slice(0, 5)}</strong>
                </div>
              </div>

              {n.diagnosis && (
                <div style={{ marginTop: 10, fontSize: 13 }}>
                  <span style={{ color: '#6B7280' }}>Διάγνωση: </span>
                  {n.diagnosis}
                </div>
              )}

              {n.adjustments && (
                <div style={{ marginTop: 6, fontSize: 13 }}>
                  <span style={{ color: '#6B7280' }}>Προσαρμογές: </span>
                  {n.adjustments}
                </div>
              )}

              {cancelled && (
                <div style={{ marginTop: 10, fontSize: 13, color: '#991B1B' }}>
                  Ακυρώθηκε {fmtDateTime(n.cancelled_at)}
                  {n.cancelled_reason ? ` — ${n.cancelled_reason}` : ''}
                </div>
              )}

              <div style={{
                marginTop: 12, paddingTop: 12, borderTop: '1px solid #F3F4F6',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
              }}>
                <div style={{ fontSize: 12, color: '#6B7280' }}>
                  {n.email_sent_at
                    ? `Εστάλη ${fmtDateTime(n.email_sent_at)}${n.email_recipient ? ` → ${n.email_recipient}` : ''}`
                    : 'Δεν έχει σταλεί με email'}
                </div>
                <button
                  onClick={() => openPdf(n.id)}
                  disabled={downloading === n.id}
                  style={{
                    background: '#C0185A', color: '#FFF', border: 'none',
                    borderRadius: 6, padding: '7px 14px', fontSize: 13, fontWeight: 600,
                    cursor: downloading === n.id ? 'default' : 'pointer',
                    opacity: downloading === n.id ? 0.6 : 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {downloading === n.id ? 'Άνοιγμα…' : 'Άνοιγμα PDF'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
